import { button, hidden } from './button.class.scss'
import { BUTTON_SIZE }    from '../../constants/button/button-size.constant'
import { BUTTON_CONFIG }  from '../../constants/button/button-config.constant'
import { GAME_DEVICES }   from '../../constants/game-devices.constant'
import { ORIENTATION }    from '../../constants/orientation/orientation.constant'
import { StateClick }     from '../stateClick/stateClick.class'
import { DESKTOP_IDS }    from '../../constants/desktopIds/desktop-ids.constant'
import { DESKTOP_EVENTS } from '../../constants/desktopEvents/desktop-events.constant'
import { Click }          from '../click/click.class'
import { NEW_SETTINGS }   from '../../constants/newSettings/new-settings.constant'
import { isObject }       from '../../functions/is-object.function'

import * as ptTextFieldLeft  from '../../assets/mobile/mb-pt-text-field-left.png'
import * as ptTextFieldRight from '../../assets/mobile/mb-pt-text-field-right.png'

/**
 * @file Defines the {@link Button} class
 * @author Joe Thompson <joe.thompson@1x2network.com>
 */

 /** Parent class for any button component for the UI */
 export class Button {
    
    /**
     * Stores reference
     * @memberof Button
     * @private
     * @instance
     * @type {button-assets}
     */
    _assets

    /**
     * Object storing click handlers for each possible state
     * @memberof Button
     * @protected
     * @instance
     * @type {state-click-events|function}
     */
    _click

    /**
     * Flag for storing whether device is desktop or mobile
     * @memberof Button
     * @protected
     * @instance
     * @type {string}
     */
    _device
    
    /**
     * Stores reference to button element
     * @memberof Button
     * @protected
     * @instance
     * @type {HTMLElement}
     */
    _elem

    /**
     * Flag for whether button is enabled/disabled
     * @memberof Button
     * @protected
     * @instance
     * @type {boolean}
     */
    _enable

    /**
     * ID of button (used to select individual button styling)
     * @memberof Button
     * @private
     * @instance
     * @type {string}
     */
    _id

    /**
     * Flag for whether UI is inverted
     * @memberof Button
     * @private
     * @instance
     * @type {boolean}
     */
    _inverted

    /**
     * Stores te side of the UI button appears
     * @memberof Button
     * @protected
     * @instance
     * @type {string}
     */
    _layout

    /**
     * Orientation of button
     * @memberof Button
     * @protected
     * @instance
     * @type {string}
     */
    _orientation

    /**
     * Current state of button
     * @memberof Button
     * @protected
     * @instance
     * @type {string}
     */
    _state

    /**
     * Possible button states
     * @memberof Button
     * @private
     * @instance
     * @type {string[]}
     */
    _states

    /**
     * Text value of button
     * @memberof Button
     * @private
     * @instance
     * @type {string}
     */
    _text

    /**
     * Stores reference to button text element
     * @memberof Button
     * @protected
     * @instance
     * @type {HTMLElement}
     */
    _textElem

    /**
     * Whether button is visible
     * @memberof Button
     * @private
     * @instance
     * @type {boolean}
     */
    _visible
    
    /**
     * Initialise core button properties
     * @param {button-assets}                 assets_       - Array of data URIs containing button assets.
     * @param {string}                        id_           - ID of button.
     * @param {string[]}                      states_       - Array of possible button states.
     * @param {boolean}                       visible_      - Whether button is initially visible.
     * @param {boolean}                       [text_=false] - Whether button has text value associated with it.
     * @param {state-click-events | function} [click_=null] - Object containg click events for each state.
     */
    constructor(assets_, id_, states_, visible_, text_ = false, click_ = null) {

        this._assets  = assets_                                //Store button assets
        this._click   = click_                                 //Store click handlers
        this._id      = id_                                    //Set button ID
        this._layout  = BUTTON_CONFIG.console[this._id].layout //Store button layout
        this._states  = states_                                //Store button states
        this._text    = ''                                     //Initialize button text
        this._visible = visible_                               //Set visibility of button

        //If mobile
        if (framework.isTouch()) {

            //Create button element
            this._elem = document.createElement('button')
            
            //Set button class
            this._elem.className = button

            //Add individual button styling
            this._elem.classList.add(this._id)

            //If button has click events for each state
            if (isObject(this._click)) {
                new StateClick(this._elem, id_, click_, this) //Create state based click handlers
            }
            
            this._orientation = ORIENTATION.orientation          //Set initial orientation
            this._device      = 'mobile'                         //Update device flag
            this._elem.classList.add(GAME_DEVICES[this._device]) //Add mobile styling
            this._elem.classList.add(this._orientation)          //Add orientation styling

            //Loop through assets
            for (let a in this._assets) {

                let str = this._assets[a].up                                //Store SVG string
                let elem = new DOMParser().parseFromString(str, 'text/xml') //Parse SVG string
                elem.documentElement.classList.add('hidden')                //Initially hide SVG asset
                this._elem.appendChild(elem.documentElement)                //Add SVG asset to button

            }

            //Button contains text
            if (text_) {

                //Create div for storing text
                this._textElem = document.createElement('div')

                //Set text id
                this.textElem.id = `${this.id}-text`

                //Add button text styling
                this.textElem.classList.add('text')

                //Add text to button
                this._elem.appendChild(this.textElem)

                //Add orientation styling to text
                this.textElem.classList.add(this._orientation)

            }

            //If game loads transparent buttons
            if (ReelConfig.transparentUI) {
                this.elem.classList.add('transparent')
            }

            this._elem.classList.add(BUTTON_SIZE[this._device][this._orientation][this._id].size) //Add button size styling
            
            //Add button to correct parent based on orientation
            ORIENTATION.orientation === 'landscape' ? document.getElementById(BUTTON_CONFIG.console[this._id].layout).appendChild(this._elem) : document.getElementById('arch').appendChild(this._elem)

            //On inverted controls setting change
            NEW_SETTINGS.controls.onChange.add(v_ => {

                this._inverted = v_ //Set flag for whether UI is inverted
                
                //If portrait and button has text
                if (ORIENTATION.orientation === 'portrait' && this.textElem) {
                    this.updateTextField() //Update text field styling
                }

            })
            
            //On orienation change
            ORIENTATION.onChange.add(v_ => {

                //If portrait and button has text
                if (v_ === 'portrait' && this.textElem) {
                    this.updateTextField() //Update text field styling
                }

            })

        }

        //Desktop
        else {
            
            this._device = 'desktop'                                     //Update device flag
            this._elem   = document.getElementById(DESKTOP_IDS[this.id]) //Store reference to button element

            this._elem.classList.add(button)                     //Add button styling
            this._elem.classList.add(GAME_DEVICES[this._device]) //Add desktop styling

            this.enable = true //Enable button

            //For each desktop mouse event
            DESKTOP_EVENTS.forEach((e_) => {

                //Add event listener
                this.elem.addEventListener(e_, (evt_) => {
                    this.backgroundImage(evt_) //Update background image of button based on event
                })

            })

            //If button has click handler
            if (this._click !== null) {

                //If button has one click handler
                if (typeof this._click === 'function') {
                    new Click(this.elem, this._click, this) //Add click handler
                }

                //If button has click handlers for each state
                else if (typeof this._click === 'object') {
                    new StateClick(this._elem, id_, click_, this) //Create state based click handlers
                }

            }

        }

    }

    /** Get device */      get device()      { return this._device }
    /** Get elem */        get elem()        { return this._elem }
    /** Get enable */      get enable()      { return this._enable }
    /** Get id */          get id()          { return this._id }
    /** Get inverted */    get inverted()    { return this._inverted }
    /** Get layout */      get layout()      { return this._layout }
    /** Get orientation */ get orientation() { return this._orientation }
    /** Get state */       get state()       { return this._state }
    /** Get text */        get text()        { return this._text }
    /** Get textElem */    get textElem()    { return this._textElem }
    /** Get visible */     get visible()     { return this._visible }

    /** Set enable */
    set enable(v_) { 

        this._enable       = v_            //Update enable flag
        this.elem.disabled = !this._enable //Enable/disable button accordingly
        
        //Enable/disable pointer events accordingly
        this.enable ? this._elem.classList.remove('disabled') : this._elem.classList.add('disabled')

        this.backgroundImage() //Update button background image
    
    }

    /** Set orientation */
    set orientation(v_) {
        this._orientation = v_
    }

    /** Set state */ 
    set state(v_) {

        //Update button state
        this._state = v_

        //Update button background image based on state
        this.backgroundImage()

    }

    /** Set text */ 
    set text(v_) { 

        let digits = v_.toString().substring(1).replace(/\./g, '') //Retrieve length of digits from currency value

        //If digits is in the millions
        if (digits.length > 8) {
            this.textElem.style.padding = '5px' //Apply styling to prevent text overlapping boundary
        }

        this._text = v_ //Update text field
        
        this.textElem.innerHTML = this._text //Update button text

    }

    /** Set visible */ 
    set visible(v_) { 

        //Update visible flag
        this._visible = v_

        //Make button visible/invisible
        this._visible ? this._elem.classList.remove(hidden) : this._elem.classList.add(hidden)

    }

    /** 
     * Updates background image of button 
     * @param {object} [e_=null] - MouseEvent object.
     */
    backgroundImage(e_ = null) {

        //If button state has been set
        if (this.state !== undefined) {

            //If mobile
            if (framework.isTouch()) {

                //If button is enabled
                if (this.enable) {

                    //Loop through button children
                    for (let i = 0; i < this.elem.children.length; i++) {
                        
                        let c = this.elem.children[i] //Store child node

                        //If node is not a div (must be SVG asset)
                        if (c.tagName !== 'DIV') {

                            //Store child node's ID
                            let id = c.id.toString()

                            //Hide assets not representing current state and show asset for new state
                            id.includes(this.state.toLowerCase()) ? c.classList.remove('hidden') : c.classList.add('hidden')

                        }

                    }

                }
        
            }

            //Desktop
            else {

                //If mouse event has been fired
                if (e_ !== null) {

                    let event = e_.type.replace(/mouse/g,'') //Match mouse event to asset state

                    //Switch on event
                    switch (event) {

                        //Mouseleave event
                        case 'leave':
                            event = 'up' //Load mouseup event asset
                            break

                        //Mouseup event
                        case 'up':
                            event = 'over' //Load mouseover event asset
                            break

                    }

                    //Handle rest of buttons
                    if (this.elem.tagName !== 'DIV') {
                        this.elem.children[0].style.backgroundImage = `url(${this._assets[this._state][event]})` //Update background image
                    }

                    //Handle menu & sound buttons
                    else {
                        this.elem.children[0].src = this._assets[this._state][event]
                    }

                }

                //No mouse event fired
                else {

                    //If button enabled
                    if (this.enable) {

                        //Handle rest of buttons
                        if (this.elem.tagName !== 'DIV') {

                            this.elem.id ==='autoPlay' ? this.elem.style.opacity = 1 : this.elem.children[0].style.opacity = 1 //Make buttons visible
                            this.elem.children[0].style.backgroundImage = `url(${this._assets[this._state].up})`               //Update background image

                        }

                        //Handle menu & sound buttons
                        else {

                            this.elem.children[0].style.opacity = 1                   //Make buttons visible
                            this.elem.children[0].src = this._assets[this._state].up //Update background image

                        }

                    }

                    //Button disabled
                    else {

                        //Handle buttons except menu & sound
                        if (this.elem.tagName !== 'DIV') {
                            this.elem.id === 'autoPlay' ? this.elem.style.opacity = 0.5 : this.elem.children[0].style.opacity = 0.5 //Adjust opacity
                        }

                    }

                }
                
            }

        }

    }

    /**
     * Handles updating the portrait text field styling
     */
    updateTextField() {

        //Set background image styling
        this.textElem.style.backgroundSize     = 'cover'
        this.textElem.style.backgroundPosition = 'center'

        //If autospin button
        if (this.id === 'autospin') {
            this.inverted ? this.textElem.style.backgroundImage = `url(${ptTextFieldRight})` : this.textElem.style.backgroundImage = `url(${ptTextFieldLeft})` //Update text field styling
        }

        //Bet button
        else {
            this.inverted ? this.textElem.style.backgroundImage = `url(${ptTextFieldLeft})` : this.textElem.style.backgroundImage = `url(${ptTextFieldRight})` //Update text field styling
        }

    }

    /** Depcreciated method */
    toggleMenu() {

        //If user is closing menu
        // if (this._elem.classList.contains('close')) {

        //     this._elem.classList.remove('close') //Remove close button styling

        //     //If portrait
        //     if (this._orientation === 'portrait') {
        //         this._elem.classList.remove('portrait_menu_open') //Remove portrait styling
        //     }

        // }

        // //User is opening menu
        // else {

        //     this._elem.classList.add('close') //Add close button styling

        //     //If portrait
        //     if (this._orientation === 'portrait') {
        //         this._elem.classList.add('portrait_menu_open') //Add portrait styling
        //     }

        // }

    }

    //BELOW METHODS ARE USED TO INTERACT WITH THE IRON DOG FRAMEWORK

    applyState(v_) {
        this.enable = v_
    }
    
    enable() {
        this.enable = true
    }

    disable() {
        this.enable = false
    }

    isButton() {
        return true
    }

    setBackGround(v_) {
        this.backgroundImage()
    }

    showButton() {
        this.visible = true
    }

    hideButton() {
        this.visible = false
    }

    getButton() {

        return {

            enable_: this.enable,
            visible: this.visible,
            name_:   this.id

        }

    }

    isEnable() {
        return this.enable
    }

    getName() {
        return framework.isTouch() ? this.id : DESKTOP_IDS[this.id]
    }

    isVisible() {
        return this.visible
    }

    setText(v_) {
        this.text = v_
    }

    applyState_MinorCurrency(s_) {}
    
    changeState(s_) {
        this.state = s_
    }

 }